import { useState } from 'react';
import axios from 'axios';
import { Link, Zap, Copy, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PLATFORMS = ['linkedin', 'twitter', 'facebook', 'instagram'];
const HOOK_STYLES = ['curiosity_gap', 'stat_shock', 'contrarian', 'story_tease', 'fomo'];

export default function WebsiteConverter() {
  const [blogTitle, setBlogTitle] = useState('');
  const [blogContent, setBlogContent] = useState('');
  const [blogUrl, setBlogUrl] = useState('');
  const [platforms, setPlatforms] = useState(['linkedin', 'twitter', 'facebook']);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState('');

  const run = async () => {
    if (!blogTitle) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/converter/generate`, {
        blog_title: blogTitle, blog_content: blogContent,
        blog_url: blogUrl, target_platforms: platforms,
      });
      setResult(data.result);
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const PLATFORM_COLORS = { linkedin: '#0a66c2', twitter: '#1da1f2', facebook: '#1877f2', instagram: '#e4405f' };

  return (
    <div className="h-full overflow-y-auto" style={{ background: 'var(--llmc-bg)' }}>
      <div className="max-w-3xl mx-auto p-6 flex flex-col gap-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2193b0, #6dd5ed)' }}>
            <Link size={20} className="text-white" />
          </div>
          <div>
            <h1 className="font-black text-white text-lg">Traffic Converter</h1>
            <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Blog → 8 curiosity-gap social posts that drive clicks back to qlia.io</p>
          </div>
        </div>

        <div className="llmc-card p-5 flex flex-col gap-4">
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--llmc-text-muted)' }}>Blog Title *</label>
            <input value={blogTitle} onChange={e => setBlogTitle(e.target.value)}
              className="llmc-input w-full px-3 py-2.5 text-sm" data-testid="converter-title-input"
              placeholder="How Zero-Trust Architecture Stops 95% of AI-Based Attacks" />
          </div>
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--llmc-text-muted)' }}>Blog URL</label>
            <input value={blogUrl} onChange={e => setBlogUrl(e.target.value)}
              className="llmc-input w-full px-3 py-2 text-sm" placeholder="https://qlia.io/blogs/..."
              data-testid="converter-url-input" />
          </div>
          <div>
            <label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--llmc-text-muted)' }}>Content Preview (optional)</label>
            <textarea value={blogContent} onChange={e => setBlogContent(e.target.value)} rows={3}
              className="llmc-input w-full px-3 py-2 text-sm resize-none" data-testid="converter-content-input"
              placeholder="Paste the first few paragraphs of your blog..." />
          </div>

          {/* Platform selection */}
          <div>
            <label className="text-xs font-semibold mb-2 block" style={{ color: 'var(--llmc-text-muted)' }}>Target Platforms:</label>
            <div className="flex gap-2 flex-wrap">
              {PLATFORMS.map(p => {
                const active = platforms.includes(p);
                const color = PLATFORM_COLORS[p];
                return (
                  <button key={p} onClick={() => setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])}
                    className="text-xs px-3 py-1.5 rounded-full capitalize font-semibold transition-all"
                    style={{
                      background: active ? `${color}22` : 'transparent',
                      border: `1px solid ${active ? color + '66' : 'rgba(255,255,255,0.1)'}`,
                      color: active ? color : 'rgba(255,255,255,0.4)',
                    }}>
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          {error && <div className="px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(235,87,87,0.1)', border: '1px solid rgba(235,87,87,0.3)', color: '#eb5757' }}>{error}</div>}

          <button onClick={run} disabled={loading || !blogTitle} data-testid="convert-btn"
            className="flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white text-sm llmc-btn-primary disabled:opacity-40">
            {loading
              ? <><div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} /> Converting...</>
              : <><Zap size={16} /> Generate Social Posts</>}
          </button>
        </div>

        {result?.posts?.length > 0 && (
          <div className="fade-in llmc-card p-5 flex flex-col gap-4">
            <h2 className="text-sm font-bold text-white">Generated Posts</h2>
            {result.ab_test_suggestion && (
              <div className="p-3 rounded-lg text-xs" style={{ background: 'rgba(240,194,127,0.08)', border: '1px solid rgba(240,194,127,0.25)', color: '#f0c27f' }}>
                A/B Test: {result.ab_test_suggestion}
              </div>
            )}
            <div className="flex flex-col gap-3">
              {result.posts.map((post, i) => {
                const color = PLATFORM_COLORS[post.platform] || '#888';
                return (
                  <div key={i} className="p-4 rounded-xl relative" data-testid={`post-result-${i}`}
                    style={{ background: `${color}0d`, border: `1px solid ${color}30` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-bold uppercase" style={{ color }}>{post.platform}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded font-medium"
                        style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>
                        {post.hook_type?.replace(/_/g, ' ')}
                      </span>
                      {post.estimated_ctr && <span className="text-[9px]" style={{ color: '#3dffa0' }}>CTR: {post.estimated_ctr}</span>}
                    </div>
                    <p className="text-xs leading-relaxed text-white whitespace-pre-wrap">{post.content}</p>
                    {post.rationale && (
                      <p className="text-[11px] mt-2" style={{ color: 'rgba(255,255,255,0.4)' }}>{post.rationale}</p>
                    )}
                    <button onClick={() => copy(post.content, `post-${i}`)} className="absolute top-3 right-3 p-1 rounded-lg"
                      style={{ opacity: 0.5 }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}>
                      {copied === `post-${i}` ? <CheckCircle size={12} style={{ color: '#3dffa0' }} /> : <Copy size={12} style={{ color: 'rgba(255,255,255,0.5)' }} />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
